import React, { useState, useEffect } from 'react';
import { Building2, Briefcase, ClipboardCheck, Key, PencilRuler, Settings, Wrench, Shield } from 'lucide-react';
import './Services.css';
import useSEO from '../hooks/useSEO';

const Services = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processData, setProcessData] = useState(null);
  const [whyChooseData, setWhyChooseData] = useState(null);

  useSEO({
    title: 'Hizmetlerimiz - Efruz İnşaat | Kapsamlı İnşaat ve Mimari Hizmetler',
    description: 'Efruz İnşaat olarak lüks konut projeleri, ticari yapılar, proje yönetimi, anahtar teslim projeler, mimari danışmanlık ve renovasyon hizmetleri sunuyoruz. Profesyonel çözümler için bize ulaşın.',
    keywords: 'inşaat hizmetleri, lüks konut projeleri, ticari yapılar, proje yönetimi, anahtar teslim, mimari danışmanlık, renovasyon, inşaat çözümleri'
  });

  const iconMap = {
    'building': Building2,
    'briefcase': Briefcase,
    'clipboard': ClipboardCheck,
    'key': Key,
    'pencil': PencilRuler,
    'cog': Settings,
    'wrench': Wrench,
    'shield': Shield
  };

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const API_BASE = process.env.REACT_APP_API_URL || '/public/api';
        const response = await fetch(`${API_BASE}/services.php`);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const text = await response.text();
        console.log('API Response:', text);
        const result = JSON.parse(text);
        if (result.success) {
          setServices(result.data || []);
        } else {
          console.error('Failed to fetch services:', result.error);
          setServices([]);
        }
      } catch (error) {
        console.error('Error fetching services:', error);
        setServices([]);
      } finally {
        setLoading(false);
      }
    };

    fetchServices();
    
    // Fetch process and why-choose data
    const fetchServiceContent = async () => {
      try {
        const API_BASE = process.env.REACT_APP_API_URL || '/public/api';
        const [processResponse, whyChooseResponse] = await Promise.all([
          fetch(`${API_BASE}/service_content.php?section=process`),
          fetch(`${API_BASE}/service_content.php?section=why-choose`)
        ]);
        
        if (processResponse.ok) {
          const processText = await processResponse.text();
          const processResult = JSON.parse(processText);
          if (processResult.success && processResult.data) {
            setProcessData(processResult.data);
          }
        }
        
        if (whyChooseResponse.ok) {
          const whyChooseText = await whyChooseResponse.text();
          const whyChooseResult = JSON.parse(whyChooseText);
          if (whyChooseResult.success && whyChooseResult.data) {
            setWhyChooseData(whyChooseResult.data);
          }
        }
      } catch (error) {
        console.error('Error fetching service content:', error);
      }
    };
    
    fetchServiceContent();
  }, []);

  return (
    <div className="services-page">
      {/* Page Title Section */}
      <section className="page-title-section">
        <div className="container">
          <h1 className="page-title">Hizmetlerimiz</h1>
          <p className="page-description">
            Kapsamlı inşaat ve mimari hizmetlerimizle projelerinizi hayata geçiriyoruz. Lüks konut projelerinden ticari yapılara, proje yönetiminden anahtar teslim çözümlere kadar geniş bir hizmet yelpazesi sunuyoruz.
          </p>
        </div>
      </section>

      {/* Services Grid */}
      <section className="services-detail section-padding">
        <div className="container">
          {loading ? (
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <p>Yükleniyor...</p>
            </div>
          ) : services.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <p>Henüz hizmet eklenmemiş.</p>
            </div>
          ) : (
            <div className="services-modern-grid">
              {services.map((service, index) => {
                const IconComponent = iconMap[service.icon] || Building2;
                return (
                  <div key={service.id} className="service-modern-card-new">
                    <div className="service-card-number">0{index + 1}</div>
                    <div className="service-card-icon-container">
                      <div className="service-card-icon-bg"></div>
                      <IconComponent size={40} className="service-card-icon" />
                    </div>
                    <div className="service-card-content">
                      <h3 className="service-card-title">{service.title}</h3>
                      <p className="service-card-description">{service.description}</p>
                    </div>
                    <div className="service-card-hover-effect"></div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* Process */}
      {processData && (
        <section className="process-section section-padding">
          <div className="container">
            <div className="section-header">
              <h2 className="heading-1">{processData.title || 'Çalışma Sürecimiz'}</h2>
              <p className="body-large">{processData.subtitle || 'Başlangıçtan teslime profesyonel yaklaşım'}</p>
            </div>
            <div className="process-timeline">
              <div className="process-timeline-line"></div>
              <div className="process-steps-container">
                {(() => {
                  try {
                    const steps = JSON.parse(processData.content || '[]');
                    return steps.map((step, index) => (
                      <div key={index} className="process-step-modern">
                        <div className="process-step-circle">
                          <span className="process-step-number">{step.number || String(index + 1).padStart(2, '0')}</span>
                        </div>
                        <div className="process-step-content">
                          <h4 className="heading-3">{step.title || ''}</h4>
                          <p className="body-regular">{step.description || ''}</p>
                        </div>
                      </div>
                    ));
                  } catch (e) {
                    return null;
                  }
                })()}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Why Choose Us */}
      {whyChooseData && (
        <section className="why-choose section-padding">
          <div className="container">
            <div className="section-header">
              <h2 className="heading-1">{whyChooseData.title || 'Neden Bizi Seçmelisiniz?'}</h2>
              <p className="body-large">{whyChooseData.subtitle || 'Farkımızı yaratan değerlerimiz'}</p>
            </div>
            <div className="why-features-grid">
              {(() => {
                try {
                  const features = JSON.parse(whyChooseData.content || '[]');
                  return features.map((feature, index) => (
                    <div key={index} className="why-feature-card">
                      <div className="why-feature-icon-wrapper">
                        <div className="why-feature-icon-bg"></div>
                        <span className="why-feature-icon">✓</span>
                      </div>
                      <h4 className="heading-3">{feature.title || ''}</h4>
                      <p className="body-regular">{feature.description || ''}</p>
                    </div>
                  ));
                } catch (e) {
                  return null;
                }
              })()}
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default Services;
